import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API } from "@/lib/api";
import { trackPixel } from "@/lib/pixel";
import { useBrands } from "./Yachts";

const GENERAL_INTERESTS = ["After Sales Services", "General Enquiry"];
const EMPTY = { name: "", email: "", phone: "", interest: "", message: "" };
const fieldClass = "rounded-none border-navy-ink/15 bg-transparent py-6 font-light text-navy-ink placeholder:text-mist/50 focus-visible:ring-gold";

export default function EnquiryForm({ presetInterest = "", onSuccess, prefix = "contact", submitLabel = "Request a Private Consultation", source = null }) {
  const [form, setForm] = useState({ ...EMPTY, interest: presetInterest });
  const [sending, setSending] = useState(false);
  const { data: brands = [] } = useBrands();

  useEffect(() => {
    if (presetInterest) setForm((f) => ({ ...f, interest: presetInterest }));
  }, [presetInterest]);

  const interests = [...brands.map((b) => b.name), ...GENERAL_INTERESTS];
  const customInterest = form.interest && !interests.includes(form.interest) ? form.interest : null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        interest: form.interest || null,
        message: form.message.trim(),
        source: source || null,
      });
      toast.success("Your enquiry has been received.", {
        description: "A member of our private office will be in touch shortly.",
      });
      trackPixel("Lead", { content_name: form.interest || "General", content_category: source || prefix });
      setForm({ ...EMPTY });
      onSuccess?.();
    } catch (err) {
      toast.error("We could not submit your enquiry.", {
        description: "Please check your details and try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`${prefix}-form`}>
      <div className="grid gap-8 sm:grid-cols-2">
        <div>
          <label className="mb-3 block text-[10px] uppercase tracking-[0.3em] text-mist" htmlFor={`${prefix}-name`}>Full Name</label>
          <Input id={`${prefix}-name`} required value={form.name} onChange={set("name")} placeholder="Your name" data-testid={`${prefix}-name-input`} className={fieldClass} />
        </div>
        <div>
          <label className="mb-3 block text-[10px] uppercase tracking-[0.3em] text-mist" htmlFor={`${prefix}-email`}>Email</label>
          <Input id={`${prefix}-email`} type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid={`${prefix}-email-input`} className={fieldClass} />
        </div>
        <div>
          <label className="mb-3 block text-[10px] uppercase tracking-[0.3em] text-mist" htmlFor={`${prefix}-phone`}>Phone</label>
          <Input id={`${prefix}-phone`} value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid={`${prefix}-phone-input`} className={fieldClass} />
        </div>
        <div>
          <label className="mb-3 block text-[10px] uppercase tracking-[0.3em] text-mist">Area of Interest</label>
          {customInterest ? (
            <div className="flex min-h-12 items-center justify-between gap-3 border border-gold/50 bg-gold/5 px-3 py-2 text-xs font-light leading-snug text-navy-ink" data-testid={`${prefix}-interest-selected`}>
              <span data-testid={`${prefix}-interest-selected-text`}>{customInterest}</span>
              <button type="button" onClick={() => setForm({ ...form, interest: "" })} className="shrink-0 text-[10px] uppercase tracking-[0.25em] text-gold hover:text-gold-light" data-testid={`${prefix}-interest-change`}>
                Change
              </button>
            </div>
          ) : (
            <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
              <SelectTrigger data-testid={`${prefix}-interest-select`} className="rounded-none border-navy-ink/15 bg-transparent py-6 font-light text-navy-ink focus:ring-gold">
                <SelectValue placeholder="Select a marque" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-navy-ink/15 bg-navy-light text-navy-ink">
                {interests.map((i) => (
                  <SelectItem key={i} value={i} data-testid={`${prefix}-interest-option-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {i}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      <div className="mt-8">
        <label className="mb-3 block text-[10px] uppercase tracking-[0.3em] text-mist" htmlFor={`${prefix}-message`}>Your Message</label>
        <Textarea id={`${prefix}-message`} required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about the yachting life you envision..." data-testid={`${prefix}-message-input`} className="rounded-none border-navy-ink/15 bg-transparent font-light text-navy-ink placeholder:text-mist/50 focus-visible:ring-gold" />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid={`${prefix}-submit-button`}
        className="mt-10 w-full border border-gold bg-gold px-9 py-4 text-[11px] uppercase tracking-[0.3em] text-navy transition-colors duration-300 hover:bg-transparent hover:text-gold disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
      >
        {sending ? "Sending..." : submitLabel}
      </button>
    </form>
  );
}
